import { changeOrganizationSession } from "./orgSwitchService";
import {
  getSessionOrganizationId,
  normalizeUserSessionModules,
} from "../utils/loginModulesUtils";
import { isSessionSuperAdmin } from "../utils/permissionUtils";
import {
  loginSessionHasQmsTrainingAdditionalRolesFlag,
  loginSessionHasSponsorCroManagementFlag,
} from "../utils/orgModuleAccess";

const HYDRATION_ATTEMPT_KEY = "aq_orgPlatformFlagsHydrationAttempt";

function hydrationAttemptKey(userData) {
  const orgId = getSessionOrganizationId(userData);
  const staffId = userData?.StaffId || "";
  return `${staffId}|${orgId}`;
}

function markHydrationAttempted(userData) {
  try {
    sessionStorage.setItem(
      HYDRATION_ATTEMPT_KEY,
      hydrationAttemptKey(userData),
    );
  } catch {
    // ignore private mode / quota
  }
}

function hasHydrationBeenAttempted(userData) {
  try {
    return (
      sessionStorage.getItem(HYDRATION_ATTEMPT_KEY) ===
      hydrationAttemptKey(userData)
    );
  } catch {
    return false;
  }
}

function deriveOrgType(userData) {
  const orgId = getSessionOrganizationId(userData);
  const staffId = userData?.StaffId || "";
  const links = userData?.OtherOrgLink;
  if (Array.isArray(links)) {
    const match = links.find(
      (row) =>
        String(row?.OrganizationId || row?.organizationId || "") ===
          String(orgId || "") &&
        String(row?.StaffId || row?.staffId || "") === String(staffId),
    );
    if (match?.OrgType) return match.OrgType;
    if (match?.orgType) return match.orgType;
  }
  return Number(userData?.IsCRO) === 1 || Number(userData?.IsCRO) === 2
    ? "CRO"
    : "Site";
}

function readPlatformFlag(session, pascalKey, camelKey) {
  if (!session || typeof session !== "object") return undefined;
  if (session[pascalKey] !== undefined) return session[pascalKey];
  if (session[camelKey] !== undefined) return session[camelKey];
  return undefined;
}

/**
 * Refresh AQ platform org flags (e.g. QMS Training Additional Roles) without re-login.
 * Uses ChangeOrganization for the current org; API enriches flags from DB when needed.
 */
export function shouldHydrateOrgPlatformFlags(userData) {
  if (!userData?.StaffId || !getSessionOrganizationId(userData)) {
    return false;
  }
  if (isSessionSuperAdmin(userData)) return false;
  /* Flag explicitly off (0/false) is valid — only hydrate when login omitted the property. */
  const hasQmsFlag = loginSessionHasQmsTrainingAdditionalRolesFlag(userData);
  const hasSponsorCroFlag = loginSessionHasSponsorCroManagementFlag(userData);
  if (hasQmsFlag && hasSponsorCroFlag) return false;
  if (hasHydrationBeenAttempted(userData)) return false;

  const mfaFlag = userData.MfaEnabled ?? userData.mfaEnabled;
  if (mfaFlag !== false && mfaFlag !== 0 && mfaFlag !== "0") {
    return false;
  }
  return true;
}

export async function hydrateOrgPlatformFlagsIfNeeded(userData) {
  if (!shouldHydrateOrgPlatformFlags(userData)) {
    return normalizeUserSessionModules({ ...userData });
  }

  markHydrationAttempted(userData);

  try {
    const response = await changeOrganizationSession(userData, {
      organizationId: getSessionOrganizationId(userData),
      staffId: userData.StaffId,
      orgType: deriveOrgType(userData),
    });

    /* MFA-on orgs return success=2 without a verified token — keep existing session flags. */
    if (Number(response?.success) !== 1 || !response?.data) {
      return normalizeUserSessionModules({ ...userData });
    }

    const session = response.data;
    const qmsFlag = readPlatformFlag(
      session,
      "QmsTrainingAdditionalRolesEnabled",
      "qmsTrainingAdditionalRolesEnabled",
    );
    const sponsorCroFlag = readPlatformFlag(
      session,
      "SponsorCroManagementEnabled",
      "sponsorCroManagementEnabled",
    );

    return normalizeUserSessionModules({
      ...userData,
      ...session,
      Sessionkey: session.Sessionkey || session.APIPWD || userData.Sessionkey,
      APIPWD: session.APIPWD || session.Sessionkey || userData.APIPWD,
      OrganizationId:
        session.OrganizationId ||
        session.Organizationid ||
        getSessionOrganizationId(userData),
      QmsTrainingAdditionalRolesEnabled:
        qmsFlag !== undefined
          ? qmsFlag
          : userData.QmsTrainingAdditionalRolesEnabled,
      SponsorCroManagementEnabled:
        sponsorCroFlag !== undefined
          ? sponsorCroFlag
          : userData.SponsorCroManagementEnabled,
      MfaEnabled:
        session.MfaEnabled ?? session.mfaEnabled ?? userData.MfaEnabled ?? true,
    });
  } catch {
    return normalizeUserSessionModules({ ...userData });
  }
}

export function clearOrgPlatformFlagsHydrationAttempt() {
  try {
    sessionStorage.removeItem(HYDRATION_ATTEMPT_KEY);
  } catch {
    // ignore
  }
}
